import Add from '@mui/icons-material/Add';
import { Box, Button, Grid, IconButton, Skeleton, Typography } from '@mui/material';
import React from 'react';
import CommonInput from '../../component/form/common-input.component';
import { FormFieldCell } from '../../component/form/form-field-cell.component';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import DialogComponent from '../../component/dialog/dialog.component';
import _ from 'lodash';
import { apiManage_rbp as apiManage } from '../../request';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import './scss/print-tail.styles.scss';
import { PrintModuleAuxiliaryModeContext } from './print-module-auxiliary-mode-context';
import {
    PrintDetailDeletePrinterButton,
    PrintDetailHeadActions,
    PrintDetailSaveButton,
} from './components/print-detail-head-buttons';

enum PrintTailDialogType {
    ADD_PRINT_TAIL = 'ADD_PRINT_TAIL',
}

class PrintTailPage extends React.Component<Props, State> {
    static contextType = PrintModuleAuxiliaryModeContext;
    context!: React.ContextType<typeof PrintModuleAuxiliaryModeContext>;

    constructor(props: Props) {
        super(props);

        this.onSave = _.debounce(this.onSave, 500, {
            leading: true,
            trailing: false,
        }).bind(this) as any;
    }

    public state: State = {
        printTails: [],
        activeMenu: '',
        printTailName: '',
        printTailDialog: false,
        printTailDialogType: undefined,
        printTailData: [],
        addPrintTailName: '',
        pageLoading: true,
        deleteAlertOpen: false,
        deleteTarget: null as any,
        deleteSubmitting: false,
        saveSubmitting: false,
        dialogConfirmLoading: false,
    };

    componentDidMount() {
        this.getPrinterDail();
    }

    /**
     * 获取打印尾列表（方法名与历史接口调用保持一致）
     */
    async getPrinterDail() {
        try {
            const res = await apiManage.getPrinterTrailers();
            if (!res?.error && Array.isArray(res)) {
                const printTails = res.map((item: any) => ({
                    title: item.descriptors_trailers_name,
                    key: `${item.descriptors_trailers_id}`,
                    dataSource: item,
                }));
                const activeDeviceItem = res.find(
                    (item: any) => `${item.descriptors_trailers_id}` === `${this.state.activeMenu}`,
                );
                const deviceItem = activeDeviceItem ? activeDeviceItem : res[0];
                const emptyLines = () => [
                    { value: '' },
                    { value: '' },
                    { value: '' },
                    { value: '' },
                    { value: '' },
                    { value: '' },
                ];
                this.setState({
                    printTails,
                    activeMenu: deviceItem?.descriptors_trailers_id != null ? `${deviceItem.descriptors_trailers_id}` : '',
                });
                if (deviceItem) {
                    this.getPrinterTailItem(deviceItem);
                } else {
                    this.setState({
                        printTailName: '',
                        printTailData: emptyLines(),
                    });
                }
            }
        } finally {
            this.setState({ pageLoading: false });
        }
    }

    getPrinterTailItem(item: any) {
        if (!item) {
            return;
        }
        const printTailData = [
            { value: item?.line_1 || '' },
            { value: item?.line_2 || '' },
            { value: item?.line_3 || '' },
            { value: item?.line_4 || '' },
            { value: item?.line_5 || '' },
            { value: item?.line_6 || '' },
        ];

        this.setState({
            printTailData,
            printTailName: item?.descriptors_trailers_name,
            activeMenu: `${item?.descriptors_trailers_id ?? ''}`,
        });
    }

    openAddPrintTailDialog = () => {
        this.setState({
            printTailDialog: true,
            printTailDialogType: PrintTailDialogType.ADD_PRINT_TAIL,
            addPrintTailName: '',
        });
    };

    /** 详情头删除：与原先列表「更多」入口共用 AlertDialog */
    openDeletePrintTailFromDetail = () => {
        const row = this.state.printTails.find((r) => String(r.key) === String(this.state.activeMenu));
        if (!row?.dataSource) {
            return;
        }
        this.setState({ deleteAlertOpen: true, deleteTarget: row.dataSource });
    };

    confirmDeletePrintTail = async () => {
        const id = this.state.deleteTarget?.descriptors_trailers_id;
        if (id == null) {
            return;
        }
        this.setState({ deleteSubmitting: true });
        try {
            await apiManage.delPrinterTrailers([Number(id)]);
            this.setState({ deleteAlertOpen: false, deleteTarget: null });
            await this.getPrinterDail();
        } finally {
            this.setState({ deleteSubmitting: false });
        }
    };

    dialogConfig(action: 'CONTENT' | 'TITLE' | 'FUN' = 'FUN') {
        const { printTailDialogType } = this.state;
        let content = <div />;
        let fun = async () => {};
        let title = '';
        switch (printTailDialogType) {
            case PrintTailDialogType.ADD_PRINT_TAIL:
                if (action === 'CONTENT') {
                    content = (
                        <FormFieldCell labelKey="print_tail_page_label">
                            <CommonInput
                                variant="outlined"
                                fullWidth
                                hiddenLabel
                                regularType="required"
                                value={this.state.addPrintTailName}
                                onChange={(e) => {
                                    this.setState({ addPrintTailName: e.target.value });
                                }}
                            />
                        </FormFieldCell>
                    );
                } else if (action === 'TITLE') {
                    title = i18n.t('print_tail_page_info');
                } else {
                    fun = async () => {
                        if (_.trim(this.state.addPrintTailName) === '') {
                            GlobalNotification.warning(i18n.t('staff_setting_page_enter_type_name'));
                            return;
                        }
                        await apiManage.postPrinterTrailers({ descriptors_trailers_name: this.state.addPrintTailName });
                        this.setState({
                            printTailDialog: false,
                            printTailDialogType: undefined,
                        });
                        this.getPrinterDail();
                    };
                }
                break;
            default:
                break;
        }
        if (action === 'CONTENT') {
            return content;
        }
        if (action === 'TITLE') {
            return title;
        }
        return fun;
    }

    async onSave() {
        const { activeMenu, printTailData, printTailName } = this.state;
        if (_.trim(printTailName) === '') {
            return GlobalNotification.error(i18n.t('staff_setting_page_enter_type_name'));
        }
        this.setState({ saveSubmitting: true });
        try {
            const res = await apiManage.patchPrinterTrailers(activeMenu, {
                descriptors_trailers_name: printTailName,
                line_1: printTailData?.[0]?.value || '',
                line_2: printTailData?.[1]?.value || '',
                line_3: printTailData?.[2]?.value || '',
                line_4: printTailData?.[3]?.value || '',
                line_5: printTailData?.[4]?.value || '',
                line_6: printTailData?.[5]?.value || '',
            });
            if (!res?.error) {
                this.getPrinterDail();
                GlobalNotification.success(i18n.t('google_text_edit_succeed'));
            }
        } finally {
            this.setState({ saveSubmitting: false });
        }
    }

    private rowHint(index: number): string | null {
        if (index === 0) {
            return i18n.t('print_tail_page_generally_welcome');
        }
        if (index === 1) {
            return i18n.t('print_tail_page_generallly_restaurant_address');
        }
        if (index === 2) {
            return i18n.t('print_tail_page_generally_resturant_phone');
        }
        return null;
    }

    render() {
        const {
            printTails,
            activeMenu,
            printTailDialog,
            printTailName,
            printTailData,
            pageLoading,
            deleteAlertOpen,
            deleteSubmitting,
            saveSubmitting,
            dialogConfirmLoading,
        } = this.state;

        if (pageLoading) {
            return <PrintTailPageSkeleton />;
        }

        const nameFieldHint = i18n.t('print_native_protocol_page_param_hint');

        return (
            <Box className="print-tail-page print-tail-page--shell">
                <Grid className="print-tail-page__layout" container spacing={3}>
                    <Grid item className="print-tail-page__list-column">
                        <Box className="print-tail-page__list-card">
                            <Box className="print-tail-page__list-head">
                                <Typography component="div" className="print-tail-page__list-title">
                                    {i18n.t('print_tail_page_label')}
                                </Typography>
                                <IconButton
                                    size="small"
                                    className="print-tail-page__list-add"
                                    onClick={() => {
                                        this.openAddPrintTailDialog();
                                    }}
                                    aria-label={i18n.t('google_text_newly_increased')}
                                >
                                    <Add />
                                </IconButton>
                            </Box>
                            <Box className="print-tail-page__list-body">
                                {printTails.map((row: any) => {
                                    const isActive = String(activeMenu) === String(row.key);
                                    return (
                                        <Box
                                            key={row.key}
                                            className={
                                                isActive
                                                    ? 'print-tail-page__list-item print-tail-page__list-item--active'
                                                    : 'print-tail-page__list-item'
                                            }
                                            onClick={() => {
                                                this.getPrinterTailItem(row.dataSource);
                                            }}
                                        >
                                            <Box className="print-tail-page__list-item-text">
                                                <Typography component="div" className="print-tail-page__list-item-name">
                                                    {row.title}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Grid>
                    {activeMenu ? (
                        <Grid item xs className="print-tail-page__detail-column" minWidth={0}>
                            <Box className="print-tail-page__detail-card">
                                <Box className="print-tail-page__detail-head">
                                    <Typography component="div" className="print-tail-page__detail-title">
                                        {i18n.t('print_tail_page_setting')}
                                    </Typography>
                                    <PrintDetailHeadActions className="print-tail-page__detail-actions">
                                        <PrintDetailDeletePrinterButton
                                            className="print-tail-page__btn-delete-printer"
                                            label={i18n.t('print_detail_delete_print_tail')}
                                            disabled={!activeMenu}
                                            onClick={() => this.openDeletePrintTailFromDetail()}
                                        />
                                        <PrintDetailSaveButton
                                            className="print-tail-page__btn-save"
                                            loading={saveSubmitting}
                                            onClick={() => void this.onSave()}
                                        >
                                            {i18n.t('job_settings_save')}
                                        </PrintDetailSaveButton>
                                    </PrintDetailHeadActions>
                                </Box>
                                <Box className="print-tail-page__detail-body">
                                    <Box className="print-tail-page__field">
                                        <Typography component="div" className="print-tail-page__field-label">
                                            {i18n.t('global_text_name')}
                                        </Typography>
                                        {this.context?.auxiliaryModeEnabled ? (
                                            <Typography component="div" className="print-tail-page__field-hint">
                                                {nameFieldHint}
                                            </Typography>
                                        ) : null}
                                        <CommonInput
                                            variant="outlined"
                                            fullWidth
                                            hiddenLabel
                                            value={printTailName}
                                            onChange={(e) => {
                                                this.setState({ printTailName: e.target.value });
                                            }}
                                        />
                                    </Box>
                                    {printTailData.map((item, index) => {
                                        const hint = this.rowHint(index);
                                        return (
                                            <Box className="print-tail-page__field" key={index}>
                                                <Typography component="div" className="print-tail-page__field-label">
                                                    {`${i18n.t('print_tail_page_row')} ${index + 1}`}
                                                </Typography>
                                                {hint && this.context?.auxiliaryModeEnabled ? (
                                                    <Typography component="div" className="print-tail-page__field-hint">
                                                        {hint}
                                                    </Typography>
                                                ) : null}
                                                <CommonInput
                                                    variant="outlined"
                                                    fullWidth
                                                    hiddenLabel
                                                    value={item.value}
                                                    onChange={(e) => {
                                                        const newPrintTailData = _.cloneDeep(printTailData);
                                                        newPrintTailData[index].value = e.target.value;
                                                        this.setState({ printTailData: newPrintTailData });
                                                    }}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Grid>
                    ) : (
                        <Grid item xs className="print-tail-page__detail-column" minWidth={0}>
                            <Box className="print-tail-page__detail-card">
                                <Typography className="print-tail-page__empty" component="div">
                                    {i18n.t('google_text_public_text_no_data')}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
                <AlertDialog
                    open={deleteAlertOpen}
                    variant="delete"
                    title={i18n.t('dinner_table_page_confirm_delete')}
                    desc={i18n.t('dinner_table_page_whether_delete')}
                    size="xs"
                    confirmLoading={deleteSubmitting}
                    onCancel={() => {
                        this.setState({ deleteAlertOpen: false, deleteTarget: null });
                    }}
                    onConfirm={() => void this.confirmDeletePrintTail()}
                />
                <DialogComponent
                    open={printTailDialog}
                    fullWidth={false}
                    onClose={() => {
                        if (!this.state.dialogConfirmLoading) {
                            this.setState({ printTailDialog: false });
                        }
                    }}
                    defaultHeaderProps={{
                        title: this.dialogConfig('TITLE') as string,
                        onClose: () => {
                            if (!this.state.dialogConfirmLoading) {
                                this.setState({ printTailDialog: false });
                            }
                        },
                    }}
                    footer={
                        <div>
                            <Button
                                color="primary"
                                variant="outlined"
                                disabled={dialogConfirmLoading}
                                onClick={() => {
                                    this.setState({ printTailDialog: false });
                                }}
                            >
                                {i18n.t('google_text_public_button_cancel')}
                            </Button>
                            <Box marginLeft={4} display="inline-block">
                                <LoadingOverlayButton
                                    type="button"
                                    color="primary"
                                    variant="contained"
                                    loading={dialogConfirmLoading}
                                    loadingIndicatorSize={22}
                                    onClick={async () => {
                                        this.setState({ dialogConfirmLoading: true });
                                        try {
                                            const fun = this.dialogConfig();
                                            if (typeof fun === 'function') {
                                                await fun();
                                            }
                                        } finally {
                                            this.setState({ dialogConfirmLoading: false });
                                        }
                                    }}
                                >
                                    {i18n.t('google_text_confirm')}
                                </LoadingOverlayButton>
                            </Box>
                        </div>
                    }
                >
                    <Grid style={{ padding: '8px 8px' }}>{this.dialogConfig('CONTENT') as React.ReactNode}</Grid>
                </DialogComponent>
            </Box>
        );
    }
}

function PrintTailPageSkeleton() {
    return (
        <Box className="print-tail-page print-tail-page--shell print-tail-page--skeleton">
            <Grid className="print-tail-page__layout" container spacing={3}>
                <Grid item className="print-tail-page__list-column">
                    <Box className="print-tail-page__list-card">
                        <Box className="print-tail-page__list-head">
                            <Skeleton className="print-tail-page__skeleton-title" variant="text" width={160} height={28} />
                            <Skeleton variant="circular" width={32} height={32} />
                        </Box>
                        <Box className="print-tail-page__list-body print-tail-page__skeleton-list">
                            {[1, 2, 3, 4, 5, 6].map((k) => (
                                <Skeleton key={k} className="print-tail-page__skeleton-list-row" variant="rounded" height={72} />
                            ))}
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs className="print-tail-page__detail-column" minWidth={0}>
                    <Box className="print-tail-page__detail-card">
                        <Box className="print-tail-page__detail-head">
                            <Skeleton variant="text" width={180} height={28} />
                            <Box display="flex" sx={{ gap: 3 }}>
                                <Skeleton variant="rounded" width={120} height={44} />
                                <Skeleton variant="rounded" width={140} height={44} />
                            </Box>
                        </Box>
                        <Box className="print-tail-page__detail-body">
                            <Box className="print-tail-page__field">
                                <Skeleton variant="text" width="35%" height={22} />
                                <Skeleton variant="text" width="55%" height={20} sx={{ mt: 0.5 }} />
                                <Skeleton variant="rounded" width="100%" height={56} sx={{ maxWidth: 400, mt: 1 }} />
                            </Box>
                            {[1, 2, 3].map((i) => (
                                <Box key={i} className="print-tail-page__field">
                                    <Skeleton variant="text" width="28%" height={22} />
                                    <Skeleton variant="rounded" width="100%" height={56} sx={{ maxWidth: 400, mt: 1 }} />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default PrintTailPage;

type State = {
    printTails: any[];
    printTailName: string;
    printTailDialog: boolean;
    printTailDialogType: PrintTailDialogType | undefined;
    activeMenu: string;
    printTailData: { value: string }[];
    addPrintTailName: string;
    pageLoading: boolean;
    deleteAlertOpen: boolean;
    deleteTarget: any;
    deleteSubmitting: boolean;
    saveSubmitting: boolean;
    dialogConfirmLoading: boolean;
};

type Props = Record<string, never>;
